// Casavio HEIC handler — converts iPhone HEIC files to JPEG client-side
// so they can be processed by Google Vision and Claude Vision Edge Functions.
//
// iPhones default to HEIC. Google Vision and Claude Vision do not accept HEIC.
// We detect HEIC by MIME type AND filename extension (browsers don't always
// report image/heic correctly), then convert in the browser.

import heic2any from 'heic2any'

const HEIC_MIMES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']

export function isHeic(file) {
  if (!file) return false
  if (HEIC_MIMES.includes(file.type)) return true
  const name = (file.name || '').toLowerCase()
  return name.endsWith('.heic') || name.endsWith('.heif')
}

/**
 * If the given File/Blob is HEIC, convert to JPEG. Otherwise return as-is.
 * Returns a File (preserves name where possible, with .jpg extension after conversion).
 * Throws if conversion fails so the caller can show an error to the user.
 *
 * @param {File} file Input file or blob
 * @param {number} quality JPEG quality 0-1, default 0.85
 * @returns {Promise<File>}
 */
export async function convertHeicIfNeeded(file, quality = 0.85) {
  if (!isHeic(file)) return file

  try {
    const converted = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality,
    })
    // heic2any returns a Blob (or Blob[] for multi-image HEIC); take first if array.
    const blob = Array.isArray(converted) ? converted[0] : converted
    const baseName = (file.name || 'photo').replace(/\.(heic|heif)$/i, '')
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' })
  } catch (err) {
    console.error('[convertHeicIfNeeded] HEIC conversion failed:', err)
    throw new Error('Could not process this iPhone photo. Please try a different photo.')
  }
}
